import { passOffset, trickWinner, type HeartsMove, type HeartsView } from "@bing/engine-hearts";
import { playSfx } from "../sound/sfx";
import {
  isVirginDeal,
  lastPlayOf,
  shownTrick,
  useCardLandSfx,
  useDealSfx,
  useTrickResultSfx,
} from "../game/use-table-sfx";
import { TRICK_HOLD_MS, useTrickHold, type TrickHold } from "../game/use-trick-hold";
import { useFrameKey } from "../game/use-frame-key";
import { LIFTED_EXTRA_PX, LIFTED_TRANSITION, useLiftToPlay } from "../game/lift-to-play";
import { getLiftToPlay } from "../settings/prefs";
import { useCompactTable, useMediaQuery } from "../game/use-compact-table";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Avatar } from "../components/Avatar";
import { Card } from "../components/Card";
import { GameIcon } from "../components/GameIcon";
import { shortName } from "../components/profile-ui";
import { DIAMOND_SEAT_EDGES, seatEdge, ThrownCard } from "../game/card-motion";
import { atX, atY, OWN_AVATAR, SEAT_AVATAR } from "../game/felt-geometry";
import {
  HAND_CARD_SCALE,
  HAND_FAN_BOX_H,
  handCardPlacement,
  handFanLayout,
} from "../game/hand-fan";
import { sortHand } from "../game/hand-sort";
import { TurnTimerSlot, type TableTurnTimer } from "../game/online-lifecycle";
import { heartsSeatFallback } from "../game/hearts-seat-label";
import { presetAvatarFor } from "../game/seat-avatar";
import { BANNER_PILL, useTransientNotice } from "../game/table-notice";
import { StatusPill, TABLE_BTN, TrickStackIcon } from "../game/table-parts";
import {
  SEAT_STACK,
  seatGap,
  seatNameBoxClass,
  TableSeat,
  TURN_GLOW,
  TURN_GLOW_STEADY,
  turnGlowSettle,
  type TableSeatProps,
} from "../game/TableSeat";
import { noticeForSeat, type SeatNotice } from "../game/seat-notice";
import { SuitGlyph } from "../components/SuitGlyph";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatAvatar,
  type SeatIdentity,
  type SeatPlayer,
} from "../game/seat-identity";

/* ------------------------------------------------------------------ *
 * Hearts (بی‌دل) game room — rebuilt (2026-07-10) on the HokmTable
 * skeleton, adapted to Hearts' own rules: a gold-ringed radial felt with
 * the three opponents pinned top/left/right (bi-del is INDIVIDUAL — no
 * teams, so seats wear neutral rings and there is no ما/حریف anywhere),
 * a 4-seat match scoreboard in the HUD (running totals toward ۱۰۰,
 * lowest wins), per-seat THIS-HAND penalty chips, the passing phase's
 * select-3 flow with a clear direction indicator, a diamond TrickCenter
 * (card-throw entrance → HOLD → sweep toward the trick WINNER → clear),
 * the sorted fanned hand (♠♥♣♦ high→low), Q♠/shoot-the-moon notices and
 * a win/lose end banner. Controls derive from the server-authoritative
 * `legal` list; the shared `Card` renders every card.
 * ------------------------------------------------------------------ */

/** How long the 3 just-received (passed-in) cards stay highlighted after the
 *  exchange — a touch longer than the engine's opening-lead pause so the glow is
 *  still up as the first card is led. */
const RECEIVE_HIGHLIGHT_MS = 2400;

/** «دل شکست» notice: total on-screen time = smooth fade-IN + ~5s hold + fade-OUT.
 *  MUST match the `hearts-broken-toast` keyframe duration so the element unmounts
 *  exactly as the fade-out finishes. */
const HEARTS_BROKEN_TOAST_MS = 5800;

/**
 * Screen-local Persian strings the shared `fa.hearts` doesn't cover yet (the
 * central i18n file is owned by a concurrent workstream — fold these into
 * `fa.hearts` when it unlocks; existing keys are reused from `fa.hearts`).
 */
const t = {
  /** Header subtitle = just «دست n» (the passing phase is self-evident on screen, so
   * the «رد کارت‌ها» suffix is dropped; the round «X از ۱۳» lives on the board chip). */
  hand: (n: number) => `دست ${faDigits(n)}`,
  roundOf: (r: number) => `دور ${faDigits(r)} از ${faDigits(13)}`,
  /** The pass-direction chip — the side the RECEIVER is drawn on (see `passSide`). On this table
   *  the cycle reads راست → چپ → روبه‌رو → no pass: the engine's left → right → across → none. */
  passLeft: "رد به چپ",
  passRight: "رد به راست",
  passAcross: "رد به روبه‌رو",
  /** Bottom status while YOUR three picks are in but others still choose. */
  waitingPass: "منتظر انتخاب بقیه…",
  /** Bottom status while another seat is on turn. */
  waitTurn: "منتظر بقیه…",
  /** The compact hearts-broken corner chip (per the rebuild spec wording). */
  heartsBroken: "دل شکست",
  /** Single-line pass-confirm CTA (the pick count now lives on the felt prompt). */
  passCta: "کارت‌ها را رد کن",
  qsTaken: (name: string, isYou: boolean) =>
    isYou ? "بی‌بی پیک را گرفتی! ۱۳+" : `${name} بی‌بی پیک را گرفت!`,
  moon: (name: string, isYou: boolean) =>
    isYou ? "همهٔ دل‌ها را بردی! بقیه ۲۶+ شدند" : `${name} همهٔ دل‌ها را برد! بقیه ۲۶+ شدند`,
  /** End-of-match banner (individual game: YOU won, or the named player did). */
  youWonBanner: "بردی!",
  wonBy: (name: string) => `${name} برد`,
} as const;

/** The self player's identity (a structural subset of the session `Profile`):
 * enough to render the OWN seat with the user's real photo + display name. */
export type SelfIdentity = SeatIdentity;

/** A real, known occupant of a seat (online play threads these from the lobby).
 * The avatar refs are the SAME pair `Avatar` takes everywhere else in the app —
 * uploaded photo when the player has one, else their chosen preset; they ride the
 * server's leak-free `TablePlayerIdentity` on every lobby roster broadcast. */
export type HeartsSeatPlayer = SeatPlayer;

/**
 * The name shown for a seat, resolved app-consistently (mirrors HokmTable): your own seat → your
 * {@link displayName} (else «شما»); another seat with a known real user → that user's name;
 * otherwise «بات n» by seat position — through the SHARED {@link heartsSeatFallback}, so the felt
 * and the end-of-match card can never number the same chair differently.
 */
function botLabel(
  view: HeartsView,
  seat: number,
  players: readonly SeatPlayer[] | undefined,
): string {
  return heartsSeatFallback(seat, view.you, view.seats, players);
}

/** Neutral seat ring — bi-del has no teams, so seats never wear a team colour;
 * only the gold turn-glow (which always wins) marks the active seat. */
const NEUTRAL_RING = "0 0 0 2px rgb(var(--c-outline-variant)), 0 4px 8px rgba(0,0,0,.4)";

/** Direction arrow for the pass chip — physical direction relative to you
 * (RTL-safe: drawn, not a glyph that mirrors). */
function PassArrowIcon({ dir, size = 13 }: { dir: "left" | "right" | "across"; size?: number }) {
  const rotate = dir === "left" ? 180 : dir === "across" ? -90 : 0;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={{ display: "block", transform: `rotate(${rotate}deg)` }}
    >
      <path d="M4 11h11.2l-4.6-4.6L12 5l7 7-7 7-1.4-1.4 4.6-4.6H4v-2z" />
    </svg>
  );
}

/**
 * How long the shoot-the-moon BANNER stays up.
 *
 * ⚠️ DELIBERATELY ITS OWN CONSTANT, AND MUCH LONGER THAN `NOTICE_MS` (`game/table-notice`, a
 * transient notice's default hold). Taking all 26 is the
 * rarest thing that happens at this table and it re-scores every other seat by +26 — three people
 * need to read WHO did it and WHAT it cost them. Measured on the deployed bundle 2026-08-29
 * (staging room `QPxbtl-VM`): the old corner pill was on screen for 2395 ms, which is about as
 * long as it takes to notice that something appeared.
 *
 * ⚠️ «Q♠ KEEPS THE SHORT PILL — IT HAPPENS EVERY SINGLE HAND» WAS THE OPERATOR'S CALL ON
 * 2026-08-29 AND THEY REVERSED IT ON 2026-09-02, from prod: «when someone takes the Queen of
 * Spades, the notice still sits behind the top player's name — move it to the same top banner
 * treatment the moon notice got, same duration». Both notices now run on THIS constant — and since
 * 2026-10-08 in the felt's bottom notices column, off the top seat (see the felt below), both in
 * the one `BANNER_PILL` (`game/table-notice`). `NOTICE_MS` survives as `useTransientNotice`'s
 * default for whatever comes next.
 */
export const MOON_BANNER_MS = 7000;

/*
 * THE MOON LINE ON A NARROW FELT — حکم's `REVEAL_NAME_NARROW` idea, measured for THIS line (the
 * harness, 2026-10-08): with the usual 16-character name «{name} همهٔ دل‌ها را برد! بقیه ۲۶+ شدند» is
 * 334–348 px, and the felt's row leaves it 264 (320) / 304 (360) / 328 (384) / 346 (402) / 356 (412)
 * — so it was cut by the pill's `truncate` at every width below 412 px, the operator's 384×823
 * included, and in RTL the cut falls on the line's END («…بقیه ۲۶+ شد…»). With 9 characters it is
 * 299–301 px and fits from 360 px up; at 320 nothing but a 2-letter name fits (a bot's whole line is
 * 254.8 px). The Q♠ line (≤ 266.5 px) wears the same cap since 2026-10-08 (Task 3's addendum): at 320
 * px its row leaves 264, and a 16-character Latin taker's line lost its «!» — one query, one cap, so
 * the two banners name a player the same way (from 360 to 411 px the Q♠ line fitted uncapped).
 */
const MOON_LINE_NARROW_QUERY = "(max-width: 411.98px)";
const MOON_NAME_NARROW = 9;

/*
 * THE FELT'S NOTICES YIELD IN A SHORT WINDOW TOO, while YOUR card is on the felt (2026-10-08, Task 3's
 * addendum — Task 4's R1 / R2, measured on tools/hearts-layout-probes). Once the diamond drops for the
 * lower top seat, your card's bottom ends 248.6 px below the felt's top (zoomed) whatever the felt's
 * height, so a short felt brings it into the column: the BOTTOM pill (28 px at `bottom-3`) on felts
 * ≤ 288.6 px — 384×569@1.5's 285 px felt, 3.6 px deep — and a row STACKED on another on felts ≤ 335.6
 * px (360×640 1.8 px, 375×667 9.3, 384×590 14.8, 384×569 28). A media query sees the WINDOW, in CSS px
 * (so a desktop zoom is included), and the felt is the window's height − 284 px − the safe insets:
 *   bottom   ≤ 589.98 px — every window without insets whose felt is ≤ 305.98 (384×569 and below;
 *            384×590's 306 px felt was measured clear);
 *   stacked  ≤ 761.98 px — a felt ≤ 335.6 with the largest insets the matrix holds (142 px, the 402 /
 *            440 iPhones: 108 + 34): 360×640, 375×667, 384×590 and below — and, needlessly but only for
 *            a stacked row, the 640–650 px Telegram Desktop windows.
 * Never at the operator's 384×823 or 412×915. A FELT-height rule would be exact, but the window is
 * what CSS can ask without measuring anything (no ResizeObserver here, by ruling).
 */
const YIELD_SHORT = {
  bottom: "[@media(max-height:589.98px)]:hidden",
  stacked: "[@media(max-height:761.98px)]:hidden",
} as const;

/*
 * …AND UNDER A TEXT ZOOM THE COLUMN'S ROOM IS MEASURED (2026-10-09, card-91's TZ triage). The two
 * rules above are exact at 100 % text, but a text zoom (raw-WebView clients get the phone's font
 * scale) grows every row of the column and drops the diamond further (its Kt is measured — TRICK_KT),
 * and neither shows in a media query, which sees the window: at ×1.3 / ×1.5 the Q♠ / moon pill or
 * «۳ کارت گرفتی» sat on the bottom of your trick card (375×667, 360×640, 384×590, 384×569, 320×568),
 * a dropped diamond's side cards reached a stacked banner (320×568), and a stacked banner reached the
 * side seats' chips with no card on the felt (320×568, 384×569/590 at ×1.5) — measured on
 * tools/hearts-layout-probes. So under a text zoom (`useNoticeRoom`: a 12 px run that computes to
 * more) the felt's height, the column's rows (`NoticeTwin` — the live rows are never measured: a
 * hidden row would shorten the column and come back, for ever) and what sits above the column — the
 * lowest card on the felt, the side seats' bottoms — are read off the layout, and a row whose top
 * would come within NOTICE_CLEAR of them yields (`zoomedNoticeYields`) ON TOP of the two rules, which
 * stay as they are. At 100 % text nothing is measured past the gauge and nothing changes.
 *   …AND THE TOP SEAT (2026-10-11, card-a5 item 10): on the shortest felts the column reaches up to it
 * — 320×492@1.5 ×1.5: «۳ کارت گرفتی» 2 px on its points chip, the matrix's last column overlap after
 * the side seats and the cards were measured — so it is measured with them (`data-top-seat`).
 */
const NOTICE_CLEAR = 2;
/** The column's rows, bottom first (`flex-col-reverse`): the base, the Q♠ above it, the moon above. */
const NOTICE_ROWS = ["base", "qs", "moon"] as const;
type NoticeRow = (typeof NOTICE_ROWS)[number];
type NoticeRows = Record<NoticeRow, boolean>;
const NO_ROWS: NoticeRows = { base: false, qs: false, moon: false };
/** A box's horizontal extent in the felt — ±Infinity where it is unlaid (it then spans the felt). */
export interface NoticeSpan {
  x0: number;
  x1: number;
}
/** Something above the column a row must clear: its bottom from the felt's top, and its extent. */
export interface NoticeObstacle extends NoticeSpan {
  bottom: number;
}
/** The felt and the column as laid out under a text zoom (all in the felt's px, from its top). */
export interface NoticeRoom {
  feltH: number;
  /** the column's `bottom` (`bottom-3`) and its row gap (`gap-1`) */
  colBottom: number;
  gap: number;
  /** each row's pill as the twin lays it out with the live words: its height and extent */
  rows: Record<NoticeRow, NoticeSpan & { h: number }>;
  /** the cards on the felt (tilted) and the seats — the side ones and the top one */
  cards: readonly NoticeObstacle[];
  seats: readonly NoticeObstacle[];
}

/** Bottom-up, as the column stacks: a shown row whose top would come within NOTICE_CLEAR of an
 *  obstacle it overlaps sideways yields, and the rows above take its place (a `hidden` row leaves
 *  the flex column). */
function yieldBelow(
  room: NoticeRoom,
  shown: NoticeRows,
  obstacles: readonly NoticeObstacle[],
): NoticeRows {
  const out = { ...NO_ROWS };
  let y = room.feltH - room.colBottom;
  for (const row of NOTICE_ROWS) {
    if (!shown[row]) continue;
    const r = room.rows[row];
    const top = y - r.h;
    const hit = obstacles.some((o) => top < o.bottom + NOTICE_CLEAR && r.x0 < o.x1 && o.x0 < r.x1);
    if (hit) out[row] = true;
    else y = top - room.gap;
  }
  return out;
}

const anyRow = (rows: NoticeRows): boolean => NOTICE_ROWS.some((row) => rows[row]);

/**
 * Which shown rows yield under a text zoom (null room: unzoomed or unmeasured — none).
 *   A SEAT (a side one, or the top one on a short felt) stays put for the notice's whole life, so
 *   there the base gives way FIRST (the pass prompt's words are on your strip's pill, the cards
 *   «۳ کارت گرفتی» points at glow in your hand), and a banner that still reaches a seat yields after
 *   it — the base then stays if it now fits.
 *   A CARD leaves with its trick: the rows yield bottom-up while it is on the felt (the stacked row
 *   first, as the window rule above has it), and come back once the felt is clear.
 */
export function zoomedNoticeYields(room: NoticeRoom | null, shown: NoticeRows): NoticeRows {
  if (room === null) return NO_ROWS;
  let live = shown;
  if (anyRow(yieldBelow(room, live, room.seats))) {
    const noBase = { ...live, base: false };
    const rest = yieldBelow(room, noBase, room.seats);
    const kept = { base: false, qs: noBase.qs && !rest.qs, moon: noBase.moon && !rest.moon };
    // …and stays when its going saves no banner (the banner yields with it gone or not)
    const withBase = { ...kept, base: live.base };
    live = anyRow(yieldBelow(room, withBase, room.seats)) ? kept : withBase;
  }
  const below = yieldBelow(room, live, room.cards);
  return {
    base: shown.base && !(live.base && !below.base),
    qs: shown.qs && !(live.qs && !below.qs),
    moon: shown.moon && !(live.moon && !below.moon),
  };
}

/*
 * ZOOMED SCREENS (operator, 2026-10-04: «do the same zoomed-screen part for بی‌دل — smaller profiles
 * (the player's own and the others'), long side names kept off the cards, and the side profiles
 * nudged toward the edges if there's room»). حکم's rule and sizes (`useCompactTable`, and the
 * SEAT_AVATAR / OWN_AVATAR of `game/felt-geometry`, the seats' through `TableSeat`): below 400 px
 * of width, or below 700 px of height with no touch pointer, every seat draws its avatar at 32 px,
 * your own too.
 * A ring adds 2 px each side, its shadow 2 more.
 */

/*
 * A SIDE SEAT'S NAME BOX stops short of the side card in front of it. MEASURED at all 20 viewports
 * (`tools/hearts-layout-probes`): that card reaches 91.7 px from the felt's centre (its 64 px slot +
 * 27.7 px of a 4°-tilted card), and the box starts 16 px in from the table's edge — so it may be
 * `half the table − 16 − 91.7 − 2` wide: `min(50vw, 220px) − 110px`, 92 px (what it always was) from
 * 404 px up, 91 on the 402 px iPhone, 82 at 384, 50 at 320 — floored at 41 px, a name's first
 * letters. (At 400–403 px the old 92 px box, 24 px in, reached the card.) حکم's own cap
 * (128) encodes ITS deal ceremony's card; بی‌دل has none. A side seat's NOTICE wears this cap too and
 * wraps (`TableSeat`'s `wrapNotice`, 2026-10-08) — hence 41, not 40: «در حال» is 32.8 px at 12 px bold,
 * and the floor box at `px-1` leaves it 33 (its `border` paints nothing: no `border-solid`).
 * Inline, not a Tailwind arbitrary class: `clamp()` inside a class name is the kind of rule that can
 * compile to nothing and redden no test. The top seat has the felt's width to itself: 92 px.
 */
const SIDE_NAME_MAX = "clamp(41px, calc(min(50vw, 220px) - 110px), 92px)";
const TOP_NAME_MAX = "92px";

/*
 * THE TRICK DIAMOND COMES IN WITH THE FELT — حکم's slot clamps (HokmTable's TRICK_SIDE / TRICK_TOP /
 * TRICK_DROP / TRICK_OWN) with بی‌دل's MEASURED constants. Each clamp's CEILING is the offset this
 * table always drew (64 / 30 / 64 px, no drop), so a roomy felt draws exactly what it did:
 *   SIDE  ≤ half the felt's width − K: the widest box a side seat draws — «در حال اتصال», never cut:
 *         72.3 px zoomed (`px-1`), 84.3 regular (`px-2.5`) — starts 4 px inside the felt, the side
 *         card reaches 27.7 px past its slot, 2 px gap → K = 106 / 118. (A NAME is capped to clear
 *         the card at 64 already, so a smaller offset only adds room — and since 2026-10-08 so is
 *         the notice, capped and wrapped like a name: K stays, an upper bound now, not the model.)
 *   TOP   ≤ half the felt's height − Kt: the top seat ends 110 px (zoomed) / 122 px below the felt's
 *         top edge in its tallest phase (avatar, name, the hand-points chip) — 18 px lower since it
 *         took حکم's `top-[38px]` (2026-10-08, it was `top-5`) — the top card's top is 40.8 px above
 *         its slot (36.3 px of tilted card + the 4.5 px its inline wrapper's line box adds), 2 px
 *         gap → Kt = 152.8 / 164.8 (MEASURED on the harness: wherever the clamp bites, the top
 *         card's top sits 2.0 px under the top seat — down to the best-effort 307×472; on 256×393
 *         no offset is left to give).
 *   DROP  …and when even 0 is not enough (a short felt: 320×568), the WHOLE diamond drops into the
 *         empty band below, exactly as far as the top seat needs, never so far that a side card
 *         leaves the felt (its 32.2 px below the slot + 2 → 50% − 34 px). 0 on every felt with room.
 *   OWN   your card 64 px below the diamond's centre — 75 where the side cards come in so far
 *         (under ~348 px of width, the 320 px class) that at 64 it sat on both of them (measured:
 *         3.7 / 6 px at 320×568; حکم's TRICK_OWN, same numbers) — never so far that it reaches the
 *         felt's bottom edge (31.8 px of tilted card below its slot + a 2 px gap).
 * CSS, not JS: `50%` inside `left`/`top` IS half the felt (the slots are positioned against it),
 * so there is nothing to measure and no frame drawn with stale numbers.
 */
const TRICK_SIDE = {
  compact: "clamp(36px, calc(50% - 106px), 64px)",
  regular: "clamp(36px, calc(50% - 118px), 64px)",
} as const;
/*
 * Kt IS MEASURED SINCE 2026-10-08 (Task 3b — the Redmi 13C report): the numbers above model the top
 * seat at 100 % text, but a text zoom (raw-WebView clients get the phone's font scale) grows its name
 * line and its points chip, not the model — at TZ ×1.3 / ×1.5 the chip sat 3.7–15.7 px on the top
 * card (360×800, 384×823). So a hidden TWIN of the top seat in its tallest phase (`TopSeatTwin`) is
 * measured, and Kt = the twin's bottom below the felt's top + TOP_CARD_REACH (40.8 px of top card
 * above its slot + the 2 px gap) — 110 / 122 + 42.8 = today's 152.8 / 164.8 at 100 % — FLOORED at
 * today's constant (TRICK_KT), so no screen ever gets less room than it had. The twin, never the
 * live seat: a notice coming or going must not move a slot. Unmeasured (the first frame, happy-dom):
 * today's Kt, so today's strings byte for byte.
 */
const TRICK_KT = { compact: 152.8, regular: 164.8 } as const;
const TOP_CARD_REACH = 42.8;
const trickTop = (kt: number): string => `clamp(0px, calc(50% - ${kt}px), 30px)`;
const trickDrop = (kt: number): string => `clamp(0px, calc(${kt}px - 50%), calc(50% - 34px))`;
const trickOwn = (drop: string, compact: boolean): string =>
  compact
    ? `clamp(0px, calc(50% - ${drop} - 34px), clamp(64px, calc((174.2px - 50vw) * 100 + 64px), 75px))`
    : `clamp(0px, calc(50% - ${drop} - 34px), 64px)`;
/** Kt for a measured top room (the twin's bottom below the felt's top), or today's when unmeasured —
 *  never below today's; rounded UP to 0.1 px (a rounded-down Kt could leave the card a hair short). */
function trickKt(room: number | null, compact: boolean): number {
  const today = compact ? TRICK_KT.compact : TRICK_KT.regular;
  if (room === null) return today;
  return Math.max(today, Math.ceil((room + TOP_CARD_REACH) * 10 - 1e-6) / 10);
}

/**
 * The trick played this round, laid out as a tight DIAMOND around the felt
 * CENTRE — each card just in FRONT of the seat that played it (you below,
 * right opponent right, top opponent above, left opponent left), so position
 * alone conveys who put down what. Ported from HokmTable's TrickCenter, and
 * the end of a trick is the same machine (`hold`, from the table's
 * `useTrickHold`): a finished trick stays on the felt for its whole hold +
 * collect from its first render, whatever newer frames say — a next lead, or a
 * pass clearing the carried trick — and only then does the live trick show.
 */
function TrickCenter({
  view,
  hold,
  compact = false,
  topRoom = null,
}: {
  view: HeartsView;
  hold: TrickHold;
  /** A zoomed screen (`useCompactTable`): the clamps for its smaller seats — see TRICK_SIDE. */
  compact?: boolean;
  /** The top seat's MEASURED bottom below the felt's top (`useTopSeatRoom`), or null — see TRICK_KT. */
  topRoom?: number | null;
}): ReactNode {
  // The side and top offsets SHRINK with the felt (TRICK_SIDE / trickTop): 64 / 30 px on a roomy
  // felt, as always, and only as far in as a small felt needs to keep every seat clear — the top
  // card pulled in toward the centre so it clears the top seat's chip, the diamond dropping when
  // even that is not enough.
  const side = compact ? TRICK_SIDE.compact : TRICK_SIDE.regular;
  const kt = trickKt(topRoom, compact);
  const top = trickTop(kt);
  const drop = trickDrop(kt);
  const own = trickOwn(drop, compact);
  const slotFor = (rel: number): CSSProperties => {
    const map: Record<number, CSSProperties> = {
      0: {
        ...atY(`calc(50% + ${drop} + ${own})`),
        left: "50%",
        transform: "translate(-50%,-50%) rotate(-3deg)",
      },
      1: {
        ...atY(`calc(50% + ${drop})`),
        ...atX(`calc(50% + ${side})`),
        transform: "translate(-50%,-50%) rotate(4deg)",
      },
      2: {
        ...atY(`calc(50% + ${drop} - ${top})`),
        left: "50%",
        transform: "translate(-50%,-50%) rotate(3deg)",
      },
      3: {
        ...atY(`calc(50% + ${drop})`),
        ...atX(`calc(50% - ${side})`),
        transform: "translate(-50%,-50%) rotate(-4deg)",
      },
    };
    return map[rel] ?? map[0]!;
  };

  const seats = view.seats;
  const displayed = shownTrick(hold.shown);

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      aria-label="trick"
      // For live probes (no visual change): set while a finished trick is held on the felt.
      data-trick-holding={hold.holding ? "1" : undefined}
      // For live probes (no visual change): the Kt in force, once the top room has been measured.
      data-room-top={topRoom === null ? undefined : kt}
    >
      {/* One layer holds the diamond; the collect-sweep animates the WHOLE layer
          toward the winner. Live + finished cards share the seat-keyed nodes, so
          a card carried from currentTrick into completedTrick keeps its identity
          (it does NOT re-throw) — only the freshly-played card throws in. */}
      <div ref={hold.layerRef} className="absolute inset-0">
        {displayed.map((play) => {
          const rel = (play.seat - view.you + seats) % seats;
          return (
            <div key={play.seat} data-trick-slot="" className="absolute" style={slotFor(rel)}>
              {/* Flies in FROM THE SEAT THAT PLAYED IT (see HokmTable's TrickCenter —
                  same diamond convention, same keying contract): `<Card thrown>`'s
                  `card-throw` keyframe always started below the viewer, so every
                  opponent's card climbed up from the bottom of the screen. */}
              <ThrownCard
                key={`${play.seat}-${play.card}`}
                edge={seatEdge(DIAMOND_SEAT_EDGES, rel)}
              >
                <Card id={play.card} large />
              </ThrownCard>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Three tiny selection dots — an opponent's passing progress (n of 3 picked). */
function PassDots({ count }: { count: number }): ReactNode {
  return (
    <span aria-label="pass-progress" className="m-0 flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`h-1.5 w-1.5 rounded-full ${i < count ? "bg-secondary" : "bg-surface-bright/60 animate-pulse"}`}
        />
      ))}
    </span>
  );
}

/** Where the top opponent sits (see the seats in `HeartsTable`) — its twin sits there too. */
const TOP_SEAT_POS = "top-[38px] left-1/2 -translate-x-1/2";
/** A seat's name box SKIN (`TableSeat` draws its frame and its truncation) — neutral, label-md,
 *  `px-2.5` on a regular screen: the box the side clamps' K was measured on (see TRICK_SIDE). The top
 *  seat's twin wears it too. */
const seatNameSkin = (compactPad: string | null): string =>
  `border-outline-variant bg-surface-container/90 ${compactPad ?? "px-2.5"} py-0.5 font-label-md text-label-md text-on-surface shadow-md`;

/** A seat's THIS-HAND points chip. `label` — the live seat's (`hand-points`); the twin's has none. */
function HandPointsChip({ points, label }: { points: number; label?: string }): ReactNode {
  return (
    <span
      aria-label={label}
      className="m-0 flex items-center gap-1 rounded-full border border-secondary/40 bg-surface-container/85 px-2 py-0.5 shadow-md"
    >
      <span className="text-danger-bluff" style={{ fontSize: 11, lineHeight: 1 }} aria-hidden>
        <SuitGlyph suit="hearts" />
      </span>
      {/* Keyed by value → re-mounts (score-bounce) whenever the take changes. */}
      <span
        key={points}
        className="m-0 inline-block font-caption text-caption text-secondary"
        style={{ animation: "score-bounce .5s ease", fontWeight: 800 }}
      >
        {faDigits(points)}
      </span>
    </span>
  );
}

/**
 * THE TOP SEAT'S TWIN — what Kt is measured on (see TRICK_KT): the top seat's own column, place and
 * parts in its TALLEST phase — the avatar's ring box, the one-line NOTICE in the name's place (the
 * taller line; the top seat's notice is never wrapped), the points chip (never the pass dots,
 * whatever the phase) — hidden from sight, touch and the a11y tree, and labelled nothing, so no
 * probe or query mistakes it for a seat. It is always that tallest seat, whatever the live one
 * shows, so a notice coming or going leaves the twin — and every slot — where it was.
 */
function TopSeatTwin({
  twinRef,
  compact,
}: {
  twinRef: { current: HTMLDivElement | null };
  compact: boolean;
}): ReactNode {
  const ring = (compact ? SEAT_AVATAR.compact : SEAT_AVATAR.regular) + 4; // the ring's 2 px padding
  return (
    <div
      ref={twinRef}
      aria-hidden="true"
      data-seat-twin="top"
      className={`pointer-events-none invisible absolute ${SEAT_STACK} ${seatGap(compact)} ${TOP_SEAT_POS}`}
    >
      <div style={{ width: ring, height: ring }} />
      {/* the NOTICE line, not a name's: a 12 px run on the 14 px box's strut is the taller line
          (at TZ ×1.5 a notice on the top seat stood 0.9 px past a name twin, measured) — its run
          styled as `SeatNoticeText`'s (hearts-layout §4b pins the two equal), without its role
          and label */}
      <span className={seatNameBoxClass(seatNameSkin, compact, true, false)}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0 }}>
          {fa.online.seatReconnecting}
        </span>
      </span>
      <HandPointsChip points={23} />
    </div>
  );
}

/**
 * The top seat's bottom below the felt's top, MEASURED off its twin from the LAYOUT (computed `top`
 * and `height` — never getBoundingClientRect: the felt's settle animates a scale over it) — null
 * until laid out (happy-dom, a hidden page): the slots then keep today's Kt. Read in a LAYOUT effect
 * (a table that mounts mid-trick paints its first frame right), again on the twin's / the felt's
 * ResizeObserver (a zoom or a web font applied late) and on `document.fonts`' `loadingdone`.
 */
function useTopSeatRoom(
  twinRef: { current: HTMLElement | null },
  feltRef: { current: HTMLElement | null },
  compact: boolean,
): number | null {
  const [room, setRoom] = useState<number | null>(null);
  useLayoutEffect(() => {
    const twin = twinRef.current;
    const felt = feltRef.current;
    if (!twin || !felt || typeof window.getComputedStyle !== "function") return undefined;
    const read = (): void => {
      const cs = window.getComputedStyle(twin);
      const top = parseFloat(cs.top);
      const h = parseFloat(cs.height);
      const feltTop = parseFloat(window.getComputedStyle(felt).top);
      if (!(h > 0) || !Number.isFinite(top) || !Number.isFinite(feltTop)) return;
      const next = top + h - feltTop;
      setRoom((prev) => (prev === next ? prev : next));
    };
    read();
    const fonts: EventTarget | undefined = document.fonts;
    fonts?.addEventListener("loadingdone", read);
    let ro: ResizeObserver | null = null;
    if (typeof window.ResizeObserver === "function") {
      ro = new window.ResizeObserver(read);
      ro.observe(twin);
      ro.observe(felt);
    }
    return () => {
      ro?.disconnect();
      fonts?.removeEventListener("loadingdone", read);
    };
  }, [twinRef, feltRef, compact]);
  return room;
}

/** The notices column's base rows (the felt's JSX) — `NoticeTwin` wears the same classes. */
const PASS_PROMPT_PILL =
  "m-0 rounded-full border border-secondary/50 bg-surface-container/90 px-3.5 py-1 font-label-md text-label-md text-secondary shadow-md max-[359px]:px-2.5 max-[359px]:text-caption max-[359px]:font-bold";
const RECEIVED_PILL =
  "m-0 flex items-center gap-1.5 rounded-full border border-secondary/60 bg-surface-container/90 px-3.5 py-1 font-label-md text-label-md text-secondary shadow-md";
/** A twin row's text, drawn by CSS (`content: attr(data-text)`): never a second copy of a live
 *  notice's words in the DOM for a query to find. */
const TWIN_TEXT = "before:content-[attr(data-text)]";

/**
 * THE NOTICES COLUMN'S TWIN (see NOTICE_CLEAR): the column's own box (`inset-x-0 px-4`, so a row
 * wraps and centres where the live one would) with its three rows in their live words — the base
 * (the pass prompt while you choose: it wraps under a zoom; else «۳ کارت گرفتی»), the Q♠ line, the
 * moon line (`BANNER_PILL`, one line) — and the GAUGE, a 12 px run: a text zoom is its computed size
 * over 12. The words are drawn by CSS (`TWIN_TEXT`), never a second copy in the DOM. Hidden from
 * sight, touch and the a11y tree, labelled nothing, never inside `felt-notices`.
 */
function NoticeTwin({
  twinRef,
  prompt,
  qsLine,
  moonLine,
}: {
  twinRef: { current: HTMLDivElement | null };
  /** the pass prompt's live text while it is the base, else null («۳ کارت گرفتی») */
  prompt: string | null;
  /** the banners' live lines (any line while one is not up: its row is then not read) */
  qsLine: string;
  moonLine: string;
}): ReactNode {
  const banner = (line: string): ReactNode => (
    <div className="flex w-full justify-center">
      <span
        className={`${BANNER_PILL} ${TWIN_TEXT}`}
        style={{ fontWeight: 800 }}
        data-text={line}
      />
    </div>
  );
  return (
    <div
      ref={twinRef}
      aria-hidden="true"
      data-notice-twin=""
      className="pointer-events-none invisible absolute inset-x-0 top-0 flex flex-col items-center gap-1 px-4"
    >
      {prompt !== null ? (
        <span className={`${PASS_PROMPT_PILL} ${TWIN_TEXT}`} data-text={prompt} />
      ) : (
        <span className={RECEIVED_PILL}>
          <span className={TWIN_TEXT} data-text="🎴" />
          <span className={TWIN_TEXT} data-text={fa.hearts.receivedCards} />
        </span>
      )}
      {banner(qsLine)}
      {banner(moonLine)}
      <span style={{ fontSize: 12 }} />
    </div>
  );
}

/** A box's extent in px from its offsets, or the whole felt when it is unlaid (happy-dom). */
const offsetSpan = (el: HTMLElement): NoticeSpan =>
  el.offsetWidth > 0
    ? { x0: el.offsetLeft, x1: el.offsetLeft + el.offsetWidth }
    : { x0: -Infinity, x1: Infinity };

/**
 * How far a trick card reaches from its slot's centre, tilted ≤ 4° about that centre: down, and to
 * either side. The CARD's own box (`.card`, its offsets summed up to the slot) — not the slot's,
 * whose height carries the card's inline wrapper's line box, and that grows with a text zoom (at
 * 360×640 ×1.3 the slot's tilted bottom sat 11 px below the card's own — the probe's quads; the
 * column yielded with room to spare). The slot's box when the card is unlaid (happy-dom).
 */
function cardReach(
  slot: Element,
  w: number,
  h: number,
  sin: number,
  cos: number,
): { down: number; side: number } {
  const card = slot.querySelector<HTMLElement>(".card");
  let x = 0;
  let y = 0;
  let el: HTMLElement | null = card;
  while (el && el !== slot) {
    x += el.offsetLeft;
    y += el.offsetTop;
    const up: Element | null = el.offsetParent;
    el = up instanceof HTMLElement && slot.contains(up) ? up : null;
  }
  if (!card || !(card.offsetHeight > 0) || el !== slot)
    return { down: (h * cos + w * sin) / 2, side: (w * cos + h * sin) / 2 };
  const ys = [y - h / 2, y + card.offsetHeight - h / 2].map(Math.abs);
  const xs = [x - w / 2, x + card.offsetWidth - w / 2].map(Math.abs);
  return {
    down: (y + card.offsetHeight - h / 2) * cos + Math.max(...xs) * sin,
    side: Math.max(...xs) * cos + Math.max(...ys) * sin,
  };
}

/** A laid-out length in px, or NaN (unlaid: happy-dom's `var(…)`, a `%`, `auto`). */
const laidPx = (v: string): number => (/^-?[\d.]+px$/.test(v) ? parseFloat(v) : NaN);
/** An obstacle's extent: centred on `cx` ± `half`, or the whole felt when unlaid. */
const extent = (x0: number, x1: number): NoticeSpan =>
  Number.isFinite(x0) && Number.isFinite(x1) ? { x0, x1 } : { x0: -Infinity, x1: Infinity };

/**
 * The column's room under a TEXT ZOOM (see NOTICE_CLEAR), read off the LAYOUT (computed lengths and
 * offsets — never getBoundingClientRect: the felt's settle animates a scale over it): null at 100 %
 * text (only the gauge is read) or while anything is unlaid (happy-dom, a first frame). Read after
 * EVERY render (a card lands, a seat's notice wraps) and on the felt's / the twin's ResizeObserver
 * and `document.fonts`' `loadingdone`. What it reads never depends on what the column hides: the
 * felt, the twin, the trick's slots (`data-trick-slot`, centred by their `translate(-50%,-50%)`,
 * tilted ≤ 4°), the side seats (`data-side-seat`, `top-[45%] -translate-y-1/2` in <main>) and the
 * top seat (`data-top-seat`, TOP_SEAT_POS: hanging from `top-[38px]`, centred by `-translate-x-1/2`).
 */
function useNoticeRoom(
  feltRef: { current: HTMLElement | null },
  columnRef: { current: HTMLElement | null },
  twinRef: { current: HTMLElement | null },
): NoticeRoom | null {
  const [room, setRoom] = useState<NoticeRoom | null>(null);
  // The room last handed to `setRoom`, compared HERE — outside React — so a measure that changed
  // nothing (every commit at 100 % text) schedules NOTHING. ⚠️ `setRoom(prev => prev)` is not
  // «nothing»: while another update of this table is still queued (a passive effect's or a timer's,
  // default-lane), React cannot bail it out early, so from this every-commit layout effect each
  // commit queued one more SYNC render — and a render that re-derives a skipped update's state never
  // bails out — until React threw #185 (card-63: staging, the first card tapped in a pass; the
  // crash unmounted the table and its socket left consented). `hearts-update-loop.test.tsx`.
  const lastRoom = useRef<NoticeRoom | null>(null);
  const readRef = useRef<() => void>(() => undefined);
  const read = (): void => {
    const felt = feltRef.current;
    const column = columnRef.current;
    const twin = twinRef.current;
    if (!felt || !column || !twin || typeof window.getComputedStyle !== "function") return;
    const cs = (el: Element): CSSStyleDeclaration => window.getComputedStyle(el);
    const measure = (): NoticeRoom | null => {
      const [base, qsRow, moonRow, gauge] = [...twin.children] as HTMLElement[];
      if (!base || !qsRow || !moonRow || !gauge) return null;
      if (!(laidPx(cs(gauge).fontSize) / 12 > 1.001)) return null;
      const feltCs = cs(felt);
      const colCs = cs(column);
      const sin = Math.sin((4 * Math.PI) / 180);
      const cos = Math.cos((4 * Math.PI) / 180);
      const cards: NoticeObstacle[] = [];
      for (const slot of felt.querySelectorAll("[data-trick-slot]")) {
        const s = cs(slot);
        const r = cardReach(slot, laidPx(s.width), laidPx(s.height), sin, cos);
        const bottom = laidPx(s.top) + r.down;
        const cx = laidPx(s.left);
        if (Number.isFinite(bottom)) cards.push({ bottom, ...extent(cx - r.side, cx + r.side) });
      }
      const seats: NoticeObstacle[] = [];
      for (const seat of felt.parentElement?.children ?? []) {
        // a side seat is centred on its `top` (`-translate-y-1/2`); the top seat hangs from its `top`
        // and is centred on its `left` (`-translate-x-1/2`)
        const side = seat.hasAttribute("data-side-seat");
        if (!side && !seat.hasAttribute("data-top-seat")) continue;
        const s = cs(seat);
        const h = laidPx(s.height);
        const w = laidPx(s.width);
        const bottom = laidPx(s.top) + (side ? h / 2 : h) - laidPx(feltCs.top);
        const x0 = laidPx(s.left) - (side ? 0 : w / 2) - laidPx(feltCs.left);
        if (Number.isFinite(bottom)) seats.push({ bottom, ...extent(x0, x0 + w) });
      }
      const row = (box: HTMLElement, pill: HTMLElement): NoticeSpan & { h: number } => ({
        h: laidPx(cs(box).height),
        ...offsetSpan(pill),
      });
      const pillOf = (wrap: HTMLElement): HTMLElement =>
        (wrap.firstElementChild as HTMLElement | null) ?? wrap;
      const next: NoticeRoom = {
        feltH: laidPx(feltCs.height),
        colBottom: laidPx(colCs.bottom),
        gap: laidPx(colCs.rowGap),
        rows: {
          base: row(base, base),
          qs: row(qsRow, pillOf(qsRow)),
          moon: row(moonRow, pillOf(moonRow)),
        },
        cards,
        seats,
      };
      const laid = [
        next.feltH,
        next.colBottom,
        next.gap,
        ...NOTICE_ROWS.map((k) => next.rows[k].h),
      ];
      return laid.every((n) => Number.isFinite(n) && n >= 0) ? next : null;
    };
    const next = measure();
    const prev = lastRoom.current;
    if (prev === next || (prev && next && JSON.stringify(prev) === JSON.stringify(next))) return;
    lastRoom.current = next;
    setRoom(next);
  };
  // after EVERY render (no deps): a card landed, a seat's notice wrapped
  useLayoutEffect(() => {
    readRef.current = read;
    read();
  });
  useLayoutEffect(() => {
    const felt = feltRef.current;
    const twin = twinRef.current;
    if (!felt || !twin) return undefined;
    const onChange = (): void => readRef.current();
    const fonts: EventTarget | undefined = document.fonts;
    fonts?.addEventListener("loadingdone", onChange);
    let ro: ResizeObserver | null = null;
    if (typeof window.ResizeObserver === "function") {
      ro = new window.ResizeObserver(onChange);
      ro.observe(felt);
      ro.observe(twin);
    }
    return () => {
      ro?.disconnect();
      fonts?.removeEventListener("loadingdone", onChange);
    };
  }, [feltRef, twinRef]);
  return room;
}

/** What a بی‌دل seat looks like — the shared `TableSeat` draws it: avatar disc + name chip, both
 * NEUTRAL (individual game — no team colours); the active seat wears the gold turn-glow. Under the
 * name, during play a compact gold chip carries the seat's THIS-HAND penalty points; during passing
 * the chip shows the seat's selection dots. */
function heartsSeatLook(
  view: HeartsView,
  seat: number,
): Pick<TableSeatProps, "onTurn" | "ringTone" | "nameSkin" | "extra"> {
  return {
    onTurn: view.phase === "playing" && view.turn === seat,
    ringTone: NEUTRAL_RING,
    nameSkin: seatNameSkin,
    extra:
      view.phase === "passing" ? (
        <PassDots count={view.selectionCounts[seat] ?? 0} />
      ) : (
        <HandPointsChip points={view.handPoints?.[seat] ?? 0} label="hand-points" />
      ),
  };
}

/**
 * The end-of-match banner — bi-del is INDIVIDUAL and lowest-total wins, so it
 * announces «بردی!» (gold) when YOUR total is the (shared) minimum, else names
 * the winner (red styling). Layered above the felt at z-[60] (above the z-55
 * chat toggle, below the z-[70] chat sheet) in addition to the WinOverlay.
 */

export function HeartsTable({
  view,
  legal,
  onMove,
  onLeave,
  self,
  players,
  timer,
  onInviteSeat,
  seatNotices,
  liftToPlay = getLiftToPlay(),
}: {
  view: HeartsView;
  legal: readonly HeartsMove[];
  onMove: (move: HeartsMove) => void;
  onLeave: () => void;
  /** The signed-in user's identity — renders the OWN (bottom) seat with the real
   * profile photo + display name. Omitted (tests) → preset avatar + «شما». */
  self?: SelfIdentity | null;
  /** Known real occupants per seat (online). Solo → undefined (bots labelled). */
  players?: readonly HeartsSeatPlayer[];
  /** The live turn / shared-pass countdown (online: the countdown, or null while nobody's turn is
   * timed; solo: never passed). Drawn in your strip's fixed slot — `TurnTimerSlot`, حکم's — in
   * every phase, the shared passing window included. */
  timer?: TableTurnTimer | null;
  /** FEATURE 3 — set (online, host, mid-game) so tapping a BOT chair invites a friend into it. */
  onInviteSeat?: ((seat: number) => void) | undefined;
  /** Online: what has just happened to each seat, said on that seat (`seat-notice.tsx`). */
  seatNotices?: readonly SeatNotice[] | undefined;
  /** «تأیید قبل از بازی کارت» (`game/lift-to-play.ts`), in PLAY only: a card tap LIFTS the card, a tap on
   *  the table (this `<main>`) plays it. The pass keeps its own selecting. Read from the Settings pref
   *  by the table itself (it cannot change under a mounted table). */
  liftToPlay?: boolean;
}): ReactNode {
  const you = view.you;
  const seats = view.seats;
  // A zoomed screen (the 384×823 phone, Telegram Desktop): smaller seats — see SEAT_AVATAR.
  const compact = useCompactTable();
  // The top seat's measured room (its hidden twin, below the felt's top) — the top card's Kt.
  const feltRef = useRef<HTMLDivElement | null>(null);
  const topTwinRef = useRef<HTMLDivElement | null>(null);
  const topRoom = useTopSeatRoom(topTwinRef, feltRef, compact);
  // The notices column's measured room under a text zoom (its twin) — see NOTICE_CLEAR.
  const noticesRef = useRef<HTMLDivElement | null>(null);
  const noticeTwinRef = useRef<HTMLDivElement | null>(null);
  const noticeRoom = useNoticeRoom(feltRef, noticesRef, noticeTwinRef);
  // One «play» cue + one send per server frame WHILE PLAYING — a card re-tapped before the server
  // confirms is ignored. Passing is local select/deselect (no commitKey), so it keeps toggling.
  const frameKey = useFrameKey(view);
  const handNumber = view.handNumber ?? 0;
  // FEATURE 3 — tap-to-invite for a bot opponent seat, only when the host may invite.
  const inviteFor = (seat: number): (() => void) | undefined =>
    onInviteSeat && players?.find((p) => p.seat === seat)?.isBot
      ? () => onInviteSeat(seat)
      : undefined;

  const passing = view.phase === "passing";
  const isYourTurn = view.phase === "playing" && view.turn === you;

  // THE END OF A TRICK IS ALWAYS SEEN (`useTrickHold`, shared with حکم): while a finished trick is
  // held on the felt, your hand's PLAYS wait — a card played into it would only queue behind the
  // collect. The pass selection is never held: it puts no card on the felt.
  const hold = useTrickHold(view, { game: "hearts" }); // the winner off its cards: no trump

  // Table sounds: a fresh deal shuffles (never on a mid-hand resume); every card
  // another seat lands clicks — including the trick's LAST card; the trick result
  // rides the collect sweep. The card and result cues read what the felt SHOWS
  // (`hold.shown`): a lead held back by the collect clicks when it appears.
  useDealSfx(view.hand.length, isVirginDeal(view));
  useCardLandSfx(lastPlayOf(hold.shown), you);
  useTrickResultSfx(hold.shown, TRICK_HOLD_MS);

  // ── Controls come straight from the server's authoritative legal list ──
  const legalPassSet = new Set<string>(legal.flatMap((m) => (m.type === "pass" ? [m.card] : [])));
  const legalPlaySet = new Set<string>(
    hold.holding ? [] : legal.flatMap((m) => (m.type === "play" ? [m.card] : [])),
  );
  // «تأیید قبل از بازی کارت» — PLAY only (the pass keeps its own select-then-confirm, so nothing can be
  // lifted while passing, nor survive the flip). Called on every render; it reads the SAME `view`
  // the hand does, through `frameKey` and `hold`.
  const liftPlay = useLiftToPlay({
    enabled: liftToPlay && !passing,
    holding: hold.holding,
    frameKey,
    playable: legalPlaySet,
    onPlay: (card) => onMove({ type: "play", card }),
  });

  // PASSING is a local select-then-confirm interaction: tapping a card toggles
  // it into the local selection; «رد کن» sends one `pass` move per pick. The
  // server-committed picks (`yourSelection` — e.g. a mid-passing RESUME) render
  // selected-but-locked; local picks top the total up to 3.
  const committed = view.yourSelection;
  const [selection, setSelection] = useState<readonly string[]>([]);
  // A new hand's passing round starts clean (and leaving `passing` clears too).
  useEffect(() => {
    setSelection([]);
  }, [handNumber, passing]);
  // The server absorbing a pick (it appears in yourSelection) drops it from the
  // local set — flicker-free: the card reads "selected" continuously. Keyed by
  // the committed CONTENT (not the per-frame array identity) and bailing out
  // with the SAME reference when nothing changed, so re-renders can't loop.
  const committedKey = committed.join(",");
  useEffect(() => {
    if (!committedKey) return;
    const absorbed = new Set(committedKey.split(","));
    setSelection((cur) => {
      const next = cur.filter((c) => !absorbed.has(c));
      return next.length === cur.length ? cur : next;
    });
  }, [committedKey]);

  const chosenCount = committed.length + selection.length;
  const canSelectMore = passing && chosenCount < 3;

  // ── RECEIVE-CARDS reveal (بی‌دل pass exchange) ────────────────────────────
  // The engine swaps the 3 passed cards in ONE step (passing → playing), so the new
  // cards would just appear and a bot could lead immediately. We DIFF the pre-exchange
  // hand (captured while still passing) against the fresh playing hand to find the 3 we
  // RECEIVED, then glow them + show a brief caption so the player sees what they got.
  // Pure client-side (received = newHand \ preExchangeHand); works solo + online, and
  // degrades to nothing on a mid-hand reconnect (no captured passing hand).
  const passingHandRef = useRef<{ handNumber: number; hand: readonly string[] } | null>(null);
  const prevPhaseRef = useRef<HeartsView["phase"] | null>(null);
  const [received, setReceived] = useState<readonly string[]>([]);
  // Capture the latest pre-exchange hand during render (stable across passing frames).
  if (passing) passingHandRef.current = { handNumber, hand: view.hand };
  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = view.phase;
    // Only a genuine passing → playing exchange, for the SAME hand, reveals new cards.
    if (view.phase !== "playing" || prev !== "passing") return;
    const cap = passingHandRef.current;
    if (!cap || cap.handNumber !== handNumber) return;
    const incoming = view.hand.filter((c) => !cap.hand.includes(c));
    if (incoming.length > 0) playSfx("deal"); // the 3 passed cards arrive
    setReceived(incoming);
  }, [view.phase, handNumber, view.hand]);
  // Hold the highlight for a beat, then let the cards settle into the fan.
  useEffect(() => {
    if (received.length === 0) return;
    const id = setTimeout(() => setReceived([]), RECEIVE_HIGHLIGHT_MS);
    return () => clearTimeout(id);
  }, [received]);
  const receivedSet = new Set(received);
  // BELOW 360 px A NOTICE YIELDS WHILE YOUR CARD IS ON THE FELT. There your played card's slot
  // reaches the felt's bottom band (75 px down, the 320 class; 2.2 px off the felt's edge at 320×568
  // once the diamond drops for the lower top seat) — the notices column's own place — so each notice
  // in it (the pass prompt, «۳ کارت گرفتی», the Q♠, the moon) is hidden there while the felt SHOWS
  // your card, and comes back for the rest of its time. Measured: the caption met your card by 24 px
  // at 320×568, a banner by 1,158 px². «Shows» is the trick hold's (`hold.shown`): the engine keeps a
  // finished trick in `completedTrick` until the next lead, long after the felt collected it, and a
  // notice must not stay hidden over an empty felt. From 360 px up only a SHORT window adds to it
  // (`YIELD_SHORT`): the bottom row, or a row stacked on another (`stacked`).
  // Under a text zoom a row also yields where its MEASURED room says so (`zoomed`, NOTICE_CLEAR).
  const yourCardShown = shownTrick(hold.shown).some((play) => play.seat === you);
  const yieldTo = (stacked: boolean, zoomed: boolean): string => {
    const short = yourCardShown
      ? `max-[359px]:hidden ${stacked ? YIELD_SHORT.stacked : YIELD_SHORT.bottom}`
      : "";
    return zoomed ? `${short} hidden`.trim() : short;
  };

  // ── «دل شکست» transient notice ────────────────────────────────────────────
  // Hearts breaking is a MOMENT, not a persistent state, so show the chip only on
  // the false→true transition: it fades in, holds ~5s, then fades out (the
  // `hearts-broken-toast` keyframe), instead of sitting on the felt the whole hand.
  // The ref inits to the first view's value so a mid-hand reconnect (already broken)
  // never re-announces; every deal clears the flag, re-arming it. The flag is monotonic
  // within a hand, so "exactly once per hand" is structural, not timed.
  //
  // ⚠️ THE TRIGGER IS THE ENGINE'S OWN FLAG AGAIN (2026-09-07). It was moved OFF it on
  // 2026-09-02 onto a client-side «has a heart been played» walk of the play-history,
  // because `breaksHearts` also counted the Q♠ and the chip fired with no heart on the
  // felt. The rule was the bug: only a heart breaks hearts now, so the flag says exactly
  // what the chip says and a second client-side copy of the rule is one more place for
  // the two to drift apart. Do not re-derive it here.
  const heartsInPlay = view.heartsBroken;
  const prevHeartsBrokenRef = useRef(heartsInPlay);
  const [heartsBrokenNotice, setHeartsBrokenNotice] = useState(false);
  useEffect(() => {
    const justBroke = heartsInPlay && !prevHeartsBrokenRef.current;
    prevHeartsBrokenRef.current = heartsInPlay;
    if (justBroke) {
      setHeartsBrokenNotice(true);
      playSfx("heartsBroken");
    }
  }, [heartsInPlay]);
  useEffect(() => {
    if (!heartsBrokenNotice) return;
    const id = setTimeout(() => setHeartsBrokenNotice(false), HEARTS_BROKEN_TOAST_MS);
    return () => clearTimeout(id);
  }, [heartsBrokenNotice]);

  const onCard = (card: string): void => {
    if (!passing) {
      if (legalPlaySet.has(card)) onMove({ type: "play", card });
      return;
    }
    setSelection((cur) =>
      cur.includes(card)
        ? cur.filter((c) => c !== card)
        : !legalPassSet.has(card) || chosenCount >= 3
          ? cur
          : [...cur, card],
    );
  };

  const confirmPass = (): void => {
    if (chosenCount < 3 || selection.length === 0) return;
    for (const card of selection) onMove({ type: "pass", card });
  };

  // Seats relative to you — HokmTable geometry: you+1 right, you+2 top, you+3 left.
  const rightSeat = (you + 1) % seats;
  const topSeat = (you + 2) % seats;
  const leftSeat = (you + 3) % seats;

  // App-consistent seat names AND faces (real user → their name + uploaded
  // photo/preset; else the positional label + preset).
  const nameOf = (seat: number): string =>
    resolveSeatName(seat, view.you, self, players, {
      you: fa.hearts.you,
      fallback: (st) => botLabel(view, st, players),
    });
  const avatarOf = (seat: number): SeatAvatar =>
    resolveSeatAvatar(seat, view.you, self, players, presetAvatarFor);
  const ownName = nameOf(you);
  const ownAvatar = avatarOf(you);

  // ── Transient notices (the felt's bottom notices column) ──
  // Q♠ captured: the public playLog names the trick + the engine's own
  // trickWinner names the taker — shown once per hand, for a beat.
  const playLog = view.playLog ?? [];
  const qsTrick = playLog.find((trick) => trick.some((p) => p.card === "QS"));
  const qsTaker = qsTrick ? trickWinner(qsTrick) : null;
  const qsSig = qsTaker !== null ? `qs-${handNumber}` : "";
  const showQsNotice = useTransientNotice(qsSig, MOON_BANNER_MS);
  const qsSoundRef = useRef("");
  useEffect(() => {
    if (!showQsNotice || qsSoundRef.current === qsSig) return;
    qsSoundRef.current = qsSig;
    playSfx("qspade"); // بی‌بی پیک landed on someone — the hand's big penalty
  }, [showQsNotice, qsSig]);

  // Shoot the moon: a finished hand scoring exactly {0, 26, 26, 26} (sum 78 —
  // impossible for a normal 26-point hand). Announced at the START of the next
  // hand (or at gameOver), keyed per hand so it fires once.
  const lastScores = view.lastHandScores;
  const moonShooter =
    lastScores.filter((s) => s === 26).length === seats - 1 && lastScores.includes(0)
      ? lastScores.indexOf(0)
      : null;
  const moonSig =
    moonShooter !== null && (handNumber > 0 || view.phase === "gameOver")
      ? `moon-${handNumber}-${view.phase === "gameOver" ? "over" : "hand"}`
      : "";
  const showMoonBanner = useTransientNotice(moonSig, MOON_BANNER_MS);
  // below 412 px the moon line names its shooter shortly (MOON_NAME_NARROW) — it is cut otherwise
  const moonLineNarrow = useMediaQuery(MOON_LINE_NARROW_QUERY);

  // ── HUD: the 4-seat MATCH scoreboard (running totals toward ۱۰۰; lowest wins).
  const scoreboardSeats = [you, rightSeat, topSeat, leftSeat];

  // «دست n» in every live phase (passing + playing); «رد کارت‌ها» dropped as
  // self-evident, and the round «X از ۱۳» lives on the felt board chip.
  const subtitle = view.phase === "gameOver" ? fa.hearts.gameOver : t.hand(handNumber + 1);

  const yourPickDone = passing && chosenCount >= 3 && selection.length === 0;
  // the notices column's rows (see the felt): the base, then the Q♠, then the moon — a row with
  // another painted under it is STACKED, and yields in a taller window (`YIELD_SHORT`)
  const feltBaseShown = (passing && !yourPickDone) || received.length > 0;
  const qsShown = showQsNotice && qsTaker !== null;
  const moonShown = showMoonBanner && moonShooter !== null;
  const zoomYield = zoomedNoticeYields(noticeRoom, {
    base: feltBaseShown,
    qs: qsShown,
    moon: moonShown,
  });
  const passPromptText = `${fa.hearts.passPrompt} (${faDigits(chosenCount)}/۳)`;
  // the banners' lines — the live pills' words, and the twin's (any line while one is not up)
  const bannerName = (seat: number): string =>
    shortName(nameOf(seat), moonLineNarrow ? MOON_NAME_NARROW : undefined);
  const qsLine = qsTaker !== null ? t.qsTaken(bannerName(qsTaker), qsTaker === you) : "";
  const moonLine = moonShooter !== null ? t.moon(bannerName(moonShooter), moonShooter === you) : "";
  const statusGold = passing ? !yourPickDone : isYourTurn;
  const statusText = passing
    ? yourPickDone
      ? t.waitingPass
      : fa.hearts.passPrompt
    : view.phase === "playing"
      ? isYourTurn
        ? fa.hearts.yourTurn
        : t.waitTurn
      : fa.hearts.gameOver;

  // Fanned-hand geometry (حکم's, `hand-fan.ts`): bigger cards (scale over `large`), the
  // whole fan lifted off the bottom edge, overlap fitting 13 cards on a phone.
  // Sorted for display (♠♥♣♦, each high→low) via the SHARED hand-sort module.
  // Bidel hand order, LEFT→RIGHT: پیک(S) · خشت(D) · گشنیز(C) · دل(H) (user request).
  const hand = sortHand(view.hand, { S: 0, D: 1, C: 2, H: 3 });
  const n = hand.length;
  const cardScale = HAND_CARD_SCALE;
  const { step, width: fanWidth } = handFanLayout(n, cardScale); // حکم's spacing (`hand-fan.ts`)
  /**
   * The fan's DEAL EPOCH: bumped on every new hand AND on the passing→playing
   * flip, so each card node re-mounts (key includes the epoch) and its staggered
   * `deal-card` entrance re-fires — a fresh deal and the pass-exchange's arriving
   * cards read as an event, never a teleport. Stable within a phase, so ordinary
   * plays never re-deal the fan.
   */
  const fanEpoch = `${handNumber}:${passing ? "pass" : "play"}`;

  const yourPoints = view.handPoints?.[you] ?? 0;

  /*
   * NO end-of-match banner here, deliberately. The table used to render one at `gameOver` ON TOP OF
   * the `WinOverlay` score summary, so finishing a match announced the SAME result twice. The modal
   * carries the scores and the actions, so it is the one that stays — loss case included. The
   * winner derivation that fed the banner went with it.
   */

  const topPad = "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))";

  /*
   * THE CHIP SAYS WHERE THE CARDS GO ON THIS SCREEN — the side the receiver's seat is DRAWN on, never
   * the engine's word. `passDir` names the engine's own seating (engine-core: clockwise, so «left» is
   * seat+1), but this table draws seat+1 on your RIGHT (حکم's diamond, above), so for two and a half
   * months «رد به چپ» sent the cards to the player drawn on the right (reported 2026-09-29). Operator:
   * keep the table, make the words match it. Read off the SAME seat variables the three `<Seat>`s
   * are pinned by, so the words, the arrow and the chair can only ever move together.
   */
  const passReceiver = (you + passOffset(view.passDir)) % seats;
  const passSide: "left" | "right" | "across" =
    passReceiver === leftSeat ? "left" : passReceiver === rightSeat ? "right" : "across";
  const passDirLabel =
    passSide === "left" ? t.passLeft : passSide === "right" ? t.passRight : t.passAcross;

  return (
    <div
      className="relative mx-auto flex w-full max-w-[440px] flex-col overflow-hidden text-on-surface select-none"
      style={{
        height: "var(--game-h)",
        background:
          "radial-gradient(circle at center,rgb(var(--c-room-light)) 0%,rgb(var(--c-surface-dim)) 100%)",
      }}
    >
      {/* HUD — exit (right in RTL), 4-seat match scoreboard (left), centred title. */}
      <header data-notice-band="" className="relative z-40 px-4" style={{ paddingTop: topPad }}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={fa.hearts.leave}
            onClick={onLeave}
            // self-start: top-align the exit at the header's paddingTop so it sits on the
            // SAME line as the fixed chat toggle (pinned at top: 10px+safe), instead of
            // being centre-pushed down by the taller 4-seat scoreboard beside it.
            className={`${TABLE_BTN} flex h-10 w-10 flex-none self-start items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95`}
          >
            <GameIcon type="exit" size={20} />
          </button>

          {/* ⚠️ THE FIXED CHAT TOGGLE'S SLOT. `TableChat`'s closed button is `position: fixed` at
              `insetInlineStart: max(0px,(100vw-440px))/2 + 64px` — the same header band, but
              OUTSIDE this flex row, so the row happily centres the title straight underneath it.
              Measured on the deployed bundle 2026-08-29: at 440 the title's right edge sat 4 px
              under the toggle, and at 320/360 `elementFromPoint` at the title's CENTRE returned
              the toggle. Reserving its width is what keeps the centred title honest. */}
          <div aria-label="chat-toggle-reserve" aria-hidden className="h-10 w-10 flex-none" />

          {/* Title + phase subtitle — the MIDDLE column. `min-w-0` + `whitespace-nowrap` +
              `truncate` so the heading can never spill OUT of its column onto the score box: with
              four long names the column was squeezed to 0 px at 320 and the 48.8 px «بی‌دل»
              overflowed it entirely (28.41 px of it landing on the box). */}
          <div className="flex min-w-0 flex-1 flex-col items-center gap-1">
            <h1 className="m-0 max-w-full truncate whitespace-nowrap font-headline-md text-headline-md text-secondary">
              {fa.hearts.title}
            </h1>
            {/* MEASURED on the deployed bundle: this pill renders 65 px wide with `px-3`, and the
                title column is 64 px at 360 — ONE pixel short, which is why it read «دس…». `px-2.5`
                brings it to 61. At 320 the column is 52 px and no padding saves it, so it is hidden
                there: the felt's own «دور X از ۱۳» chip already carries the round. */}
            <span className="m-0 hidden max-w-full truncate rounded-full border border-secondary/30 bg-surface-container/60 px-2.5 py-0.5 font-label-md text-label-md text-on-surface min-[360px]:block">
              {subtitle}
            </span>
          </div>

          {/* Match scores toward ۱۰۰ — one column per seat, yours highlighted.
              (Individual game: four columns, deliberately NOT a ما/حریف box.)

              ⚠️ THE WIDTH IS FIXED AND MUST STAY FIXED. It used to be `flex-none` with each name
              capped at `max-w-14` (56 px), so four long Persian names made the box 255.2 px —
              measured — and, because `flex-none` never shrinks, that width came straight out of
              the title's column: 0 px of column left at 320, where the box was also pushed 7.2 px
              off the left edge, clipping the player's OWN score. With a declared width the four
              columns share it (`flex-1 min-w-0` + `truncate`), so a long name shortens itself
              instead of pushing the header around. The step at 360 is what lets the common phone
              widths keep a readable name while 320 still fits the title. */}
          <div
            aria-label="match-scores"
            className="flex w-[132px] flex-none items-center gap-1.5 rounded-lg border border-outline-variant bg-surface-container/80 px-2 py-1.5 shadow-md min-[360px]:w-[160px] min-[412px]:w-[196px]"
          >
            {scoreboardSeats.map((seat, i) => {
              const score = view.totalScores[seat] ?? 0;
              const mine = seat === you;
              return (
                <div
                  key={seat}
                  className={`flex min-w-0 flex-1 flex-col items-center leading-none ${i > 0 ? "border-r border-outline-variant/60 pr-1.5" : ""}`}
                >
                  <span
                    className="m-0 w-full truncate text-center font-caption text-caption text-on-surface-variant"
                    style={{ fontSize: 10 }}
                  >
                    {mine ? fa.hearts.you : nameOf(seat)}
                  </span>
                  {/* Keyed by value → re-mounts (score-bounce) on change. */}
                  <span
                    key={score}
                    className={`m-0 font-headline-sm text-headline-sm ${mine ? "text-primary" : "text-on-surface"}`}
                    style={{ animation: "score-bounce .5s ease" }}
                  >
                    {faDigits(score)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </header>

      {/* Felt playfield — with «تأیید قبل از بازی کارت» on, a tap anywhere on it plays the lifted card
          (a tap on a button inside it, the bot seat's invite «+», stays that button's). */}
      <main
        className="relative min-h-0 flex-1"
        onClick={liftToPlay ? liftPlay.tapTable : undefined}
      >
        {/* The gold-ringed radial felt (HokmTable's). */}
        <div
          ref={feltRef}
          className="absolute inset-3 overflow-hidden rounded-[40px]"
          style={{
            background:
              "radial-gradient(circle,rgb(var(--c-primary-container)) 0%,rgb(var(--c-felt-deep)) 100%)",
            boxShadow:
              "inset 0 8px 32px rgb(var(--c-felt-shadow) / .8), 0 0 0 6px rgb(var(--c-gold-deep)), 0 12px 48px rgba(0,0,0,.6)",
          }}
        >
          {/* ONE aligned top chip row on the felt (the Hokm corner-chip spot):
              playing → the round counter (start) + the «دل شکست» chip once
              hearts break (end); passing → the pass-direction chip. */}
          {view.phase === "playing" ? (
            <div className="absolute inset-x-3 top-3 z-20 flex items-center justify-between gap-2">
              <div
                aria-label="hand-rounds"
                className="flex items-center gap-2 rounded-full border border-secondary/40 bg-surface-container/85 px-2 py-0.5 shadow-lg"
                style={{ transform: "translateX(-8px)" }} // nudge a touch off the felt edge (user request)
              >
                <span className="text-secondary" aria-hidden>
                  <TrickStackIcon />
                </span>
                <span className="m-0 font-caption text-caption text-on-surface">
                  {t.roundOf(view.trickNumber + 1)}
                </span>
              </div>
              {heartsBrokenNotice ? (
                <span
                  aria-label="hearts-broken"
                  className="m-0 flex items-center gap-1 rounded-full border border-danger-bluff/50 bg-danger-bluff/15 px-2.5 py-0.5 font-caption text-caption text-danger-bluff shadow-lg"
                  // Smooth fade-IN → ~5s hold → fade-OUT, then it unmounts (transient
                  // moment, not a persistent chip). Duration ↔ HEARTS_BROKEN_TOAST_MS.
                  style={{
                    animation: `hearts-broken-toast ${HEARTS_BROKEN_TOAST_MS}ms ease both`,
                    fontWeight: 800,
                  }}
                >
                  <GameIcon type="heart" size={12} />
                  {t.heartsBroken}
                </span>
              ) : null}
            </div>
          ) : passing ? (
            <div className="absolute inset-x-3 top-6 z-20 flex items-center justify-start">
              <span
                key={`dir-${handNumber}`}
                aria-label="pass-direction"
                className="m-0 flex items-center gap-1.5 rounded-full border border-secondary/40 bg-surface-container/85 px-2.5 py-0.5 font-caption text-caption text-secondary shadow-lg"
                style={{ animation: "pop-in-glow .4s ease both", fontWeight: 800 }}
              >
                <PassArrowIcon dir={passSide} />
                {passDirLabel}
              </span>
            </div>
          ) : null}

          <TrickCenter view={view} hold={hold} compact={compact} topRoom={topRoom} />

          {/* THE FELT'S NOTICES COLUMN — its bottom-centre row, where «۳ کارت گرفتی» shows (operator,
              2026-10-08: «notices that now appear over the top player's profile move to the bottom
              strip, where «۳ کارت گرفتی» shows — e.g. «فلانی بی‌بی پیک را گرفت»»). ONE column,
              `flex-col-reverse` — its FIRST child is its BOTTOM one:
                the base   the pass prompt while you choose, or «۳ کارت گرفتی» after the exchange
                           (never both: one is the pass, the other play);
                the Q♠     «… بی‌بی پیک را گرفت!» above it;
                the moon   «… همهٔ دل‌ها را برد! …» above that. A moon hand's Q♠ trick is still in the
                           playLog at gameOver, so the two can be up together — stacked, never drawn
                           over each other.
              The two banners lived at the TOP of the table from 2026-08-29 / 09-02, out of the felt
              and at z-50, because inside it, at its top, the felt's `overflow-hidden` clipped them and
              the top seat painted over them (17.8 % covered, measured) — and up there they sat over
              the top seat themselves (576–704 px² of its ring at every size). At the felt's BOTTOM no
              seat reaches the band, the corner's radius leaves the row's 16 px inset clear, and z-20
              paints over the trick layer (it has no z-index) — tools/hearts-layout-probes measures
              all of it. Below 360 px, or in a short window, each notice yields to your own played
              card (`yieldTo`). */}
          <div
            ref={noticesRef}
            data-testid="felt-notices"
            // For live probes (no visual change): the room measured under a text zoom, if any.
            data-notice-room={noticeRoom === null ? undefined : JSON.stringify(noticeRoom)}
            className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex flex-col-reverse items-center gap-1 px-4"
          >
            {/* The pass prompt — a table prompt, only while you're still choosing; once your three
                are in, the strip's pill takes over with «منتظر انتخاب بقیه…».
                Below 360 px it is one type step smaller (caption, still bold) with less padding: at
                label-md the line is 253.5 px, and it wrapped to two lines on the 320 px felt while the
                countdown sat beside it (measured, tools/hearts-layout-probes). The countdown has left
                the felt (your strip's slot, below); the step stays for the narrowest felts. */}
            {passing && !yourPickDone ? (
              <span
                className={`${PASS_PROMPT_PILL} ${yieldTo(false, zoomYield.base)}`}
                style={{ animation: "pop-in-glow .35s ease both" }}
              >
                {passPromptText}
              </span>
            ) : null}
            {/* Receive beat: a brief «۳ کارت گرفتی» caption pointing at the glowing cards just
                passed into the hand. */}
            {received.length > 0 ? (
              <span
                className={`${RECEIVED_PILL} ${yieldTo(false, zoomYield.base)}`}
                style={{ animation: "pop-in-glow .35s ease both" }}
              >
                <span aria-hidden>🎴</span>
                {fa.hearts.receivedCards}
              </span>
            ) : null}
            {/* The Q♠ and the moon: the same gold {@link BANNER_PILL}, held {@link MOON_BANNER_MS}
                (operator, 2026-09-07: «same treatment, same design»), told apart by their words. */}
            {qsShown && qsTaker !== null ? (
              <div
                aria-label="queen-notice"
                className={`flex w-full justify-center ${yieldTo(feltBaseShown, zoomYield.qs)}`}
              >
                <span
                  className={BANNER_PILL}
                  style={{ animation: "pop-in-glow .35s ease both", fontWeight: 800 }}
                >
                  {qsLine}
                </span>
              </div>
            ) : null}
            {moonShown && moonShooter !== null ? (
              <div
                aria-label="moon-banner"
                className={`flex w-full justify-center ${yieldTo(feltBaseShown || qsShown, zoomYield.moon)}`}
              >
                <span
                  className={BANNER_PILL}
                  style={{ animation: "pop-in-glow .35s ease both", fontWeight: 800 }}
                >
                  {moonLine}
                </span>
              </div>
            ) : null}
          </div>
          <NoticeTwin
            twinRef={noticeTwinRef}
            prompt={passing && !yourPickDone ? passPromptText : null}
            qsLine={qsLine || "ب"}
            moonLine={moonLine || "ب"}
          />
        </div>

        {/* Seats: the across opponent top, the others left/right — the Hokm pinning (inward from
            the edge, a touch toward the corners). THE SIDE OPPONENTS sit 16 px past the safe-area
            inset (it was 24 — حکم's nudge, 2026-10-04: «the side profiles nudged toward the edges
            if there's room»; their ring keeps ≥ 1 px off the felt's gold ring, measured), their
            name box capped by SIDE_NAME_MAX so it stops short of the card in front of them.
            THE TOP OPPONENT sits at حکم's `top-[38px]` (operator, 2026-10-08: «the profiles sit too
            close to the screen edges: use the same spacing as حکم» — at `top-5` its ring was 8 px
            below the felt's top edge, حکم's 26); the trick diamond's top clamp moved with it (Kt). */}
        <TableSeat
          {...heartsSeatLook(view, topSeat)}
          name={nameOf(topSeat)}
          avatar={avatarOf(topSeat)}
          posClass={TOP_SEAT_POS}
          onInvite={inviteFor(topSeat)}
          notice={noticeForSeat(seatNotices, topSeat)}
          compact={compact}
          nameMax={TOP_NAME_MAX}
          markTopSeat
        />
        <TableSeat
          {...heartsSeatLook(view, rightSeat)}
          name={nameOf(rightSeat)}
          avatar={avatarOf(rightSeat)}
          posClass="top-[45%] right-[calc(max(env(safe-area-inset-right),var(--tg-safe-right,0px))_+_16px)] -translate-y-1/2"
          onInvite={inviteFor(rightSeat)}
          notice={noticeForSeat(seatNotices, rightSeat)}
          compact={compact}
          nameMax={SIDE_NAME_MAX}
          wrapNotice
          markSideSeat
        />
        <TableSeat
          {...heartsSeatLook(view, leftSeat)}
          name={nameOf(leftSeat)}
          avatar={avatarOf(leftSeat)}
          posClass="top-[45%] left-[calc(max(env(safe-area-inset-left),var(--tg-safe-left,0px))_+_16px)] -translate-y-1/2"
          onInvite={inviteFor(leftSeat)}
          notice={noticeForSeat(seatNotices, leftSeat)}
          compact={compact}
          nameMax={SIDE_NAME_MAX}
          wrapNotice
          markSideSeat
        />
        <TopSeatTwin twinRef={topTwinRef} compact={compact} />
      </main>

      {/* Bottom local player panel */}
      <div
        className="relative z-30 flex flex-col gap-2 bg-gradient-to-t from-surface-container-lowest via-surface-container-lowest/80 to-transparent px-3 pt-2"
        style={{
          paddingBottom: "calc(14px + var(--game-safe-bottom))",
        }}
      >
        {/* Identity + status row — the OWN seat shows the real profile photo +
            display name; your this-hand points ride a compact chip beside it.
            `min-w-0` down the name's side is what lets a long name truncate when the pill or the
            pass button needs the room — the other group never shrinks (`flex-none`) and its pill
            never wraps (measured at master: «منتظر انتخاب بقیه…» on THREE lines at 320 px, the
            strip 66 px past the screen and the pass button off it).
            It is حکم's NOTICE STRIP too since 2026-10-08: the turn timer's one fixed slot rides at
            its far end (below). A height FLOOR — the tallest the row ever drew at 100 %, your name
            over your points (24 + 2 + 16), your own ring 42 on a regular screen — so neither the
            pass (no points line, the 40 px button) nor the play can move the row's centre, the line
            your avatar and the timer are centred on (it was 36 / 40 / 42 by phase, measured). A
            floor, not a fixed box (review 6b F1): under a WebView text zoom the column outgrows 42
            (×1.5 ≈ 62) and a fixed box spilled your points chip onto the lifted card below. */}
        <div className="flex min-h-[42px] items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <div
              className="flex items-center justify-center rounded-full"
              style={{
                padding: 2,
                boxSizing: "border-box",
                // re-pulses on EVERY turn of yours (`turnGlowSettle`); steady, no settle, while a
                // finished trick is held — the lead re-pulses when the hand is live
                boxShadow: statusGold
                  ? hold.holding
                    ? TURN_GLOW_STEADY
                    : TURN_GLOW
                  : NEUTRAL_RING,
                animation:
                  statusGold && !hold.holding
                    ? turnGlowSettle(view.phase, view.trickNumber)
                    : undefined,
              }}
            >
              {/* Same resolution as the pinned seats (own seat → the self profile). */}
              <Avatar
                avatarId={ownAvatar.avatarId}
                avatarPhotoId={ownAvatar.avatarPhotoId}
                size={compact ? OWN_AVATAR.compact : OWN_AVATAR.regular}
                official={ownAvatar.official}
              />
            </div>
            <div className="flex min-w-0 flex-col items-start gap-0.5 leading-tight">
              {/* `min(168px, 100%)`: a nowrap name's min-content IS its full width, so a plain
                  168 px cap never lets it shrink below 168 when the row runs short — it spills
                  under the pill instead. `dir="auto"` keeps a Latin name's START (see `Seat`).
                  A zoomed screen draws it one step smaller (headline-sm → body-lg), as حکم does. */}
              <span
                dir="auto"
                className={`m-0 inline-block truncate ${compact ? "font-body-lg text-body-lg" : "font-headline-sm text-headline-sm"} text-primary`}
                style={{ maxWidth: "min(168px, 100%)" }}
              >
                {ownName}
              </span>
              {view.phase !== "passing" ? (
                <span
                  aria-label="your-points"
                  className="m-0 flex items-center gap-1 font-caption text-caption text-on-surface-variant"
                >
                  <span className="text-danger-bluff" aria-hidden>
                    <SuitGlyph suit="hearts" />
                  </span>
                  <span
                    key={yourPoints}
                    className="inline-block"
                    style={{ animation: "score-bounce .5s ease" }}
                  >
                    {faDigits(yourPoints)}
                  </span>
                </span>
              ) : null}
            </div>
            {view.phase === "passing" ? (
              // review 6b F1 — THE ROW'S HEIGHT IN THE PASS: an invisible, zero-width twin of the
              // play's name-over-points column, so a zoomed row (a floor, above) is as tall in the
              // pass as in the play and the row's centre — your avatar's and the timer's line —
              // does not move between phases. `-ms-2` cancels its `gap-2`: it takes no width, and
              // at 100 % its 42 px is the floor itself, so nothing moves there.
              <span
                aria-hidden="true"
                data-points-twin=""
                className="invisible -ms-2 flex w-0 flex-col items-start gap-0.5 overflow-hidden leading-tight"
              >
                <span
                  className={`m-0 inline-block ${compact ? "font-body-lg text-body-lg" : "font-headline-sm text-headline-sm"}`}
                >
                  {"\u00a0"}
                </span>
                <span className="m-0 flex items-center gap-1 font-caption text-caption">
                  <span>
                    <SuitGlyph suit="hearts" />
                  </span>
                  <span className="inline-block">{faDigits(0)}</span>
                </span>
              </span>
            ) : null}
          </div>

          <div className="flex flex-none items-center gap-2">
            {passing && !yourPickDone ? (
              // The yellow pass box: single line «کارت‌ها را رد کن», in the profile row on the
              // LEFT, beside the timer's slot. (It was nudged 6 px further left, which would now eat
              // into the slot's gap.) The «select 3» instruction + progress moved to the felt
              // (bottom-centre) so this stays a clean one-line action.
              <button
                type="button"
                onClick={confirmPass}
                disabled={chosenCount < 3 || selection.length === 0}
                className={`${TABLE_BTN} whitespace-nowrap rounded-lg bg-secondary px-5 py-2 font-body-md text-body-md text-ink-on-gold transition-all enabled:hover:bg-gold-deep enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-50`}
                style={{
                  fontWeight: 800,
                  boxShadow: "inset 0 2px 4px rgba(255,255,255,.3), 0 4px 8px rgba(0,0,0,.5)",
                }}
              >
                {t.passCta}
              </button>
            ) : (
              <StatusPill gold={statusGold}>{statusText}</StatusPill>
            )}
            {/* THE TURN TIMER'S ONE FIXED SLOT — حکم's (operator, 2026-10-08: «the turn timer gets
                one fixed slot opposite the player's profile, as in حکم»): the LAST child of this
                group, the strip's physical LEFT end under rtl, your avatar at the other. Reserved
                on every online table from its first frame (empty while nobody's turn is timed),
                never in solo. It carries the shared passing window too, for the WHOLE pass — your
                three picks in or not.
                ⚠️ THAT WINDOW IS NOT «25s», WHICH IT HAS NOT BEEN FOR A LONG TIME AND WAS NEVER ONE
                NUMBER. بی‌دل's pass is the only card-game SIMULTANEOUS phase (the server broadcasts
                `turn { seat: -1 }`) and its window is the table's turn timer PLUS FIVE
                (`HEARTS_PASS_WINDOW_EXTRA_SEC`, operator 2026-09-20): a PUBLIC table runs
                `PUBLIC_TURN_TIMER_SEC` = 10 ⇒ 15 s, a private one the host's own pick + 5. Nothing
                here hardcodes it — `totalSec` rides the frame. Read the constants, never this line. */}
            <TurnTimerSlot timer={timer} />
          </div>
        </div>

        {/* Fanned hand — sorted, only the legal cards tappable (lifted + gold
            pulse); a passing pick lifts higher. Every card node is keyed by the
            DEAL EPOCH so a fresh hand AND the post-exchange hand re-mount with
            the staggered `deal-card` entrance (never popping in). */}
        <div aria-label="your hand" className="flex w-full justify-center overflow-visible px-2">
          <div
            className="relative"
            style={{ height: HAND_FAN_BOX_H, width: fanWidth, maxWidth: "100%" }}
          >
            {hand.map((card, i) => {
              const isCommitted = passing && committed.includes(card);
              const isPicked = passing && (selection.includes(card) || isCommitted);
              const tappable = passing
                ? (legalPassSet.has(card) && chosenCount < 3) || selection.includes(card)
                : legalPlaySet.has(card);
              // A just-received (passed-in) card during the reveal beat: lifted + glowing.
              const isReceived = !passing && receivedSet.has(card);
              // «تأیید قبل از بازی کارت» (play only): the lifted card stands LIFTED_EXTRA_PX over the legal
              // rise; while one is lifted the other legal cards rest at 0 and stop pulsing. A received
              // card keeps its 20 px reveal — a 2.4 s highlight, not a legality cue.
              const isLifted = liftPlay.lifted === card;
              // A PASS PICK STANDS AS HIGH AS A LIFTED CARD, NO HIGHER (card-f5 item 4, operator 2026-10-13:
              // «بی‌دل, 384×832, passing: the pass button sits 3 px over a hand card»). At its old 30 px a pick
              // reached 6.8 px up into the strip row, where the pass button — 1 px off the row's bottom — sat
              // on the picks below it: the 6th card of 13 by 3.4 px in that solo report, more on an online
              // strip, where the timer's slot puts the button over the fan's middle. The lifted card's height
              // is the one `lift-geometry.test.ts` proves clear of that row; the gold ring still tells a pick
              // from the 10 px of a card you may pick.
              const rise =
                isPicked || isLifted
                  ? 18 + LIFTED_EXTRA_PX
                  : isReceived
                    ? 20
                    : tappable && !passing && liftPlay.lifted === null
                      ? 18
                      : canSelectMore && tappable
                        ? 10
                        : 0;
              return (
                <div
                  key={`${fanEpoch}-${card}`}
                  data-received={isReceived ? "true" : undefined}
                  className="absolute bottom-0"
                  style={{
                    // its place on the fan's arc, `rise` px over it (`hand-fan.ts`)
                    ...handCardPlacement(i, n, step, rise, cardScale),
                    zIndex: isReceived ? 80 + i : isPicked ? 100 + i : i,
                    // the lifted card rises on an easing that never overshoots (LIFTED_TRANSITION)
                    transition: isLifted
                      ? LIFTED_TRANSITION
                      : "transform .2s cubic-bezier(.175,.885,.32,1.275)",
                    // Gold glow on the 3 received cards (filter-only — never transform,
                    // which would clobber the fan positioning above).
                    animation: isReceived
                      ? `receive-glow ${RECEIVE_HIGHLIGHT_MS}ms ease both`
                      : undefined,
                  }}
                >
                  {liftToPlay && !passing ? (
                    // the hook owns the latch and the one «play» cue (on the table tap): the card is
                    // silent and carries no commitKey
                    <Card
                      id={card}
                      large
                      dealIndex={i}
                      selected={isLifted}
                      disabled={!tappable}
                      silent
                      lifted={isLifted}
                      resting={liftPlay.lifted !== null && !isLifted}
                      onClick={() => liftPlay.tapCard(card)}
                    />
                  ) : (
                    <Card
                      id={card}
                      large
                      dealIndex={i}
                      selected={isPicked}
                      disabled={!tappable && !isPicked}
                      commitKey={passing ? undefined : frameKey}
                      onClick={isCommitted ? undefined : () => onCard(card)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* End-of-match banner — the prominent «بردی!» / «{name} برد» moment, in
          addition to the WinOverlay summary rendered by the container. */}
    </div>
  );
}
